import { ReactSortable } from 'react-sortablejs';
import classnames from 'classnames';
import { LeftOutlined, RightOutlined } from '@easyv/react-icons';
import { Dropdown, Input, Menu } from '@easyv/react-components';
import * as React from 'react';
import { isNil } from '@/utils';
import { DropListMenuOption, getDropListMenu } from '../DropListMenu';

export interface SortableTabsDataSourceType {
  id: string;
  name: string;
  rank: number;
}

export interface SortableTabsProp {
  className?: string;
  dataSource: SortableTabsDataSourceType[];
  selectKey?: string;
  droplist?: DropListMenuOption[];
  onTabChange?: (key: string) => void;
  isSortable?: boolean;
  isCanRename: boolean;
  renamingStateId: string | undefined;
  onRenameStateStart: (id: string) => void;
  onRenameStateEnd: (name: string) => void;
}

export function SortableTabs({
  className,
  dataSource,
  selectKey,
  onTabChange,
  droplist,
  isCanRename,
  isSortable = false,
  renamingStateId,
  onRenameStateStart,
  onRenameStateEnd,
}: SortableTabsProp) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<(HTMLElement | null)[]>([]);
  const [innerDataSource, setInnerDataSource] = React.useState<SortableTabsDataSourceType[]>([]);
  const [leftDropList, setLeftDropList] = React.useState<React.ReactNode>();
  const [rightDropList, setRightDropList] = React.useState<React.ReactNode>();

  const handleScroll = React.useCallback(
    (stateId: string) => {
      if (!scrollRef.current) return;
      const i = dataSource.findIndex((a) => a.id === stateId);
      if (i === -1) return;

      const itemRef = listRef.current[i];
      if (isNil(itemRef)) return;
      if (!itemRef) return;
      /** 没有在视野外，不滚动 */
      if (
        itemRef.offsetLeft > scrollRef.current.scrollLeft &&
        itemRef.offsetLeft + itemRef.offsetWidth <
          scrollRef.current.scrollLeft + scrollRef.current.offsetWidth
      )
        return;

      scrollRef.current.scrollTo({
        left: itemRef.offsetLeft,
        behavior: 'smooth',
      });
    },
    [dataSource],
  );

  const switchToPrev = () => {
    const i = dataSource.findIndex((a) => a.id === selectKey);
    if (i === -1) return;
    if (i - 1 >= 0) {
      onTabChange?.(dataSource[i - 1].id);
    }
  };

  const switchToNext = () => {
    const i = dataSource.findIndex((a) => a.id === selectKey);
    if (i === -1) return;
    if (i + 1 < dataSource.length) {
      onTabChange?.(dataSource[i + 1].id);
    }
  };

  React.useEffect(() => {
    selectKey && handleScroll(selectKey);
    // 避免比如切换状态时也会导致触发这里的逻辑
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectKey]);

  React.useEffect(() => {
    setInnerDataSource(JSON.parse(JSON.stringify(dataSource)));
  }, [dataSource]);

  React.useEffect(() => {
    const calcHiddenItems = () => {
      const leftHiddenItems = dataSource.filter((_, index) => {
        const node = listRef.current[index];
        if (!node || !scrollRef.current) return false;
        return node.offsetLeft < scrollRef.current.scrollLeft;
      });

      const rightHiddenItems = dataSource.filter((_, index) => {
        const node = listRef.current[index];
        if (!node || !scrollRef.current) return false;
        return (
          node.offsetLeft + node.clientWidth >
          scrollRef.current.scrollLeft + scrollRef.current.clientWidth
        );
      });

      const leftDropList = (
        <Menu>
          {leftHiddenItems.map((item) => {
            return (
              <Menu.Item
                key={item.id}
                onClick={() => {
                  onTabChange?.(item.id);
                }}
              >
                <div className='w-full text-center'>{item.name}</div>
              </Menu.Item>
            );
          })}
        </Menu>
      );

      const rightDropList = (
        <Menu>
          {rightHiddenItems.map((item) => {
            return (
              <Menu.Item
                key={item.id}
                onClick={() => {
                  onTabChange?.(item.id);
                }}
              >
                <div className='w-full text-center'>{item.name}</div>
              </Menu.Item>
            );
          })}
        </Menu>
      );

      setLeftDropList(leftDropList);
      setRightDropList(rightDropList);
    };

    calcHiddenItems();
    const scrollerNode = scrollRef.current;
    scrollerNode?.addEventListener('scroll', calcHiddenItems);

    return () => {
      scrollerNode?.removeEventListener('scroll', calcHiddenItems);
    };
  }, [dataSource, handleScroll, onTabChange]);

  return (
    <div className={classnames('flex rounded-sm h-9 items-center sortable-tabs', className)}>
      <Dropdown trigger='hover' position='bl' droplist={leftDropList}>
        <div
          className='cursor-pointer  flex justify-center items-center hover:bg-bgColorHover rounded-[10px] w-5 h-5 bg-FFFFFF0A'
          onClick={switchToPrev}
        >
          <LeftOutlined size={12} />
        </div>
      </Dropdown>
      <div ref={scrollRef} className='flex-1 overflow-x-auto no-scroll-container relative'>
        <ReactSortable
          disabled={!isSortable}
          list={innerDataSource}
          setList={setInnerDataSource}
          className='flex'
        >
          {innerDataSource.map((item, index) => (
            <Dropdown
              key={item.id}
              droplist={droplist && getDropListMenu(droplist)}
              trigger={'contextMenu'}
            >
              <div
                ref={(node) => {
                  listRef.current[index] = node;
                }}
                key={item.id}
                className={classnames(
                  'px-[6px] cursor-pointer whitespace-pre hover:text-white flex justify-center items-center',
                  item.id === selectKey ? 'text-white' : '',
                )}
                onClick={() => {
                  onTabChange?.(item.id);
                }}
                onContextMenu={() => {
                  onTabChange?.(item.id);
                }}
                onDoubleClick={() => {
                  isCanRename && onRenameStateStart(item.id);
                }}
              >
                {renamingStateId === item.id ? (
                  <Input
                    autoFocus
                    maxLength={24}
                    className='!w-20 !h-6'
                    defaultValue={item.name}
                    onBlur={(e: any) => {
                      const value = e.target.value;
                      onRenameStateEnd(value);
                    }}
                  />
                ) : (
                  <div
                    className={classnames(
                      'max-w-32',
                      'whitespace-nowrap',
                      'block',
                      'overflow-hidden',
                      'text-ellipsis',
                    )}
                  >
                    {item.name}
                  </div>
                )}
              </div>
            </Dropdown>
          ))}
        </ReactSortable>
      </div>

      <Dropdown trigger='hover' position='br' droplist={rightDropList}>
        <div
          className='cursor-pointer  flex justify-center items-center hover:bg-bgColorHover rounded-[10px] w-5 h-5 bg-FFFFFF0A'
          onClick={switchToNext}
        >
          <RightOutlined size={12} />
        </div>
      </Dropdown>
    </div>
  );
}
